import React from "react";
import { MapPin, Phone, Mail, Globe, Building2, MessageCircle, ArrowRight } from "lucide-react";
import { PageHero, PageBody, FONT } from "./_shared";
import { useBul } from "./BulLayout";
import { instituteHref, isExternalUrl } from "@/lib/instituteUrl";
import InstituteMap from "@/components/InstituteMap";

export default function Contact() {
  const data = useBul();
  const inst = data.institute || {};

  const address = [inst.address1, inst.address2, inst.city, inst.district, inst.state, inst.pincode]
    .filter(Boolean)
    .join(", ");
  const phones = [...new Set([inst.admission_phone, inst.contact_phone].filter(Boolean))];

  return (
    <div data-testid="bul-contact">
      <PageHero eyebrow="Get in Touch" title="Contact Us" />
      <PageBody>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white border-t-4 border-[#FDBB30]" data-testid="bul-contact-card">
            <div className="bg-[#0F0F10] text-white p-8">
              <div className="w-11 h-11 border-2 border-[#FDBB30] flex items-center justify-center text-[#FDBB30] mb-3">
                <Building2 size={20} strokeWidth={1.6} />
              </div>
              <h3 className="text-2xl leading-tight" style={{ fontFamily: FONT.serif, fontWeight: 700 }}>{inst.name || "Institute"}</h3>
              {inst.code && (
                <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-[#FDBB30] font-semibold">
                  Institute Code · {inst.code}
                </div>
              )}
            </div>
            <div className="p-8 space-y-5 text-[15px]">
              {address && (
                <div className="flex items-start gap-3" data-testid="bul-contact-address">
                  <MapPin size={16} strokeWidth={1.6} className="text-[#FDBB30] mt-1 shrink-0" />
                  <span className="text-[#2A2A2B] leading-relaxed">{address}</span>
                </div>
              )}
              {phones.length > 0 && (
                <div className="flex items-start gap-3" data-testid="bul-contact-phones">
                  <Phone size={16} strokeWidth={1.6} className="text-[#FDBB30] mt-1 shrink-0" />
                  <span className="text-[#2A2A2B]">
                    {phones.map((p, i) => (
                      <React.Fragment key={`${p}-${i}`}>
                        <a href={`tel:${p.replace(/\s+/g, "")}`} className="text-[#0F0F10] hover:text-[#E19E14] underline decoration-[#FDBB30] underline-offset-4">
                          {p}
                        </a>
                        {i < phones.length - 1 && " · "}
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              )}
              {inst.whatsapp_phone && (
                <div className="flex items-start gap-3" data-testid="bul-contact-whatsapp">
                  <MessageCircle size={16} strokeWidth={1.6} className="text-[#FDBB30] mt-1 shrink-0" />
                  <a
                    href={`https://wa.me/${inst.whatsapp_phone.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0F0F10] hover:text-[#E19E14] underline decoration-[#FDBB30] underline-offset-4"
                  >
                    WhatsApp · {inst.whatsapp_phone}
                  </a>
                </div>
              )}
              {inst.email && (
                <div className="flex items-start gap-3" data-testid="bul-contact-email">
                  <Mail size={16} strokeWidth={1.6} className="text-[#FDBB30] mt-1 shrink-0" />
                  <a href={`mailto:${inst.email}`} className="text-[#0F0F10] hover:text-[#E19E14] underline decoration-[#FDBB30] underline-offset-4 break-all">
                    {inst.email}
                  </a>
                </div>
              )}
              {inst.website_slug && (
                <div className="flex items-start gap-3" data-testid="bul-contact-website">
                  <Globe size={16} strokeWidth={1.6} className="text-[#FDBB30] mt-1 shrink-0" />
                  <a
                    href={isExternalUrl(inst.website_slug) ? inst.website_slug : `https://gsmandal.org${instituteHref(inst.website_slug)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0F0F10] hover:text-[#E19E14] underline decoration-[#FDBB30] underline-offset-4"
                  >
                    {isExternalUrl(inst.website_slug)
                      ? inst.website_slug.replace(/^https?:\/\//i, "")
                      : `gsmandal.org/${inst.website_slug}`}
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="bg-[#FDBB30] text-[#0F0F10] p-8 flex flex-col gap-5" data-testid="bul-contact-actions">
            <h3 className="text-2xl leading-tight" style={{ fontFamily: FONT.serif, fontWeight: 700 }}>Quick Actions</h3>
            <p className="text-sm leading-relaxed">Reach the admissions team directly, or explore our campus virtually.</p>
            <div className="mt-2 flex flex-col gap-2">
              {inst.admission_phone && (
                <a
                  href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`}
                  className="inline-flex items-center justify-between gap-2 bg-[#0F0F10] hover:bg-[#1F1F20] text-white font-semibold uppercase tracking-[0.2em] text-[11px] px-4 py-3"
                  data-testid="bul-contact-action-call"
                >
                  <span className="inline-flex items-center gap-2"><Phone size={14} strokeWidth={2.4} /> Call Admissions</span>
                  <ArrowRight size={13} strokeWidth={2.4} />
                </a>
              )}
              {inst.email && (
                <a
                  href={`mailto:${inst.email}`}
                  className="inline-flex items-center justify-between gap-2 bg-white hover:bg-[#F8F5EE] text-[#0F0F10] font-semibold uppercase tracking-[0.2em] text-[11px] px-4 py-3"
                  data-testid="bul-contact-action-email"
                >
                  <span className="inline-flex items-center gap-2"><Mail size={14} strokeWidth={2.4} /> Email Us</span>
                  <ArrowRight size={13} strokeWidth={2.4} />
                </a>
              )}
              {inst.whatsapp_phone && (
                <a
                  href={`https://wa.me/${inst.whatsapp_phone.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-between gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold uppercase tracking-[0.2em] text-[11px] px-4 py-3"
                  data-testid="bul-contact-action-whatsapp"
                >
                  <span className="inline-flex items-center gap-2"><MessageCircle size={14} strokeWidth={2.4} /> WhatsApp</span>
                  <ArrowRight size={13} strokeWidth={2.4} />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#726C61] font-semibold mb-4">Find us on map</div>
          <InstituteMap
            latitude={inst.latitude}
            longitude={inst.longitude}
            address={address}
            testid="bul-contact-map"
            className="w-full aspect-[16/9] overflow-hidden border border-[#D9D2C1] bg-[#EDE7DA]"
          />
        </div>
      </PageBody>
    </div>
  );
}
